import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isProgressChangedError } from './roundProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import ClassroomActivity from './wheel/ClassroomActivity';
import ClassroomTrainActivity from './wheel/ClassroomTrainActivity';
import TrainRoundedIcon from '@mui/icons-material/TrainRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  completeRound,
  recordMathPresentationAnswer,
  fetchBookPointEvents,
  beginPreparedRound,
  completeCargoSortingRound,
  completeDigitRecallRound,
  completeMemoryRound,
  completeOddOneOutRound,
  completePatternRecognitionRound,
  completeReflexRound,
  fetchBoard,
  fetchReadonlyLibrary,
  fetchReadonlyStarGarden,
  recoverWordBuildRound,
  previewRound,
  startRound,
  timeoutWordBuildRound,
} from './miniTasksApi';
import { clearMiniTaskCountdown } from './useMiniTaskCountdown';
import { readStoredBoardSession, writeStoredBoardSession } from './sessionStorage';
import { saveTeacherSession, teacherRequest } from './teacherModeApi';
import TeacherPinDialog from './components/TeacherPinDialog';
import BoardHeader from './components/BoardHeader';
import { abandonTournament, getCurrentTournament } from './tournamentApi';
import { localTournamentStorageKey } from './tournamentLocalMatch';
import {
  playMiniTaskSound, prepareMiniTaskSound, readMiniTaskSoundEnabled, writeMiniTaskSoundEnabled,
} from './soundEffects';
import { prepareMiniTaskRoundAssets, releaseMiniTaskRoundAssets } from './roundAssets';
import BoardErrorState from './components/BoardErrorState';
import ClassStoryPanel from './components/ClassStoryPanel';
import LibraryReadOnlyDialog from './components/LibraryReadOnlyDialog';
import StarGardenReadOnlyDialog from './components/StarGardenReadOnlyDialog';
import LibraryPointAnimations from '../admin/library/components/LibraryPointAnimations';
import RoundSummary from './components/RoundSummary';
import RoundView from './components/RoundView';
import useListeningAnswer, { restoredListeningProgress } from './listening/useListeningAnswer';
import StudentGrid from './components/StudentGrid';
import RandomStudentSelectorModal from './components/random-student-selector/RandomStudentSelectorModal';
import ClassroomTimerModal from './components/ClassroomTimerModal';
import useRandomStudentSelector from './components/random-student-selector/useRandomStudentSelector';
import MemoryRoundView from './memory/MemoryRoundView';
import ReflexRoundView from './reflex/ReflexRoundView';
import DigitRecallRoundView from './digit-recall/DigitRecallRoundView';
import OddOneOutRoundView from './odd-one-out/OddOneOutRoundView';
import CargoSortingRoundView from './cargo-sorting/CargoSortingRoundView';
import TurkishL1RoundView from './sound-hunt/TurkishL1RoundView';
import TurkishL1TourView from './sound-hunt/TurkishL1TourView';
import PatternRecognitionRoundView from './pattern-recognition/PatternRecognitionRoundView';
import WeeklyReadingRoundView from './weekly-reading/WeeklyReadingRoundView';
import WordBuildRoundView from './word-build/WordBuildRoundView';
import { playLocalWordBuildAttempt } from './word-build/wordBuildLocalRound';
import { countCorrectEvents } from './odd-one-out/oddOneOutBoard';
import { patternRecognitionPreviewSummary } from './pattern-recognition/patternRecognitionState';
import {
  clearActiveWordBuildRound,
  clearWordBuildLocalProgress,
  readActiveWordBuildRound,
  readWordBuildLocalProgress,
  writeActiveWordBuildRound,
  writeWordBuildLocalProgress,
} from './word-build/wordBuildSessionStorage';
import useLatestRequestGuard from './useLatestRequestGuard';
import { BOARD_COOLDOWN_RETRY_MS, nextBoardCooldownRefreshDelay } from './boardCooldownRefresh';
import { buildWordBuildPreviewAttempt } from './word-build/wordBuildPreview';

const ANSWER_FEEDBACK_DELAY_MS = 2200;
const boardActionButtonSx = {
  minHeight: { xs: 52, sm: 56, md: 58 },
  borderRadius: 99,
  px: { xs: 2.2, sm: 2.7, md: 3.1 },
  bgcolor: '#7c3aed',
  color: '#fff',
  border: '2px solid rgba(255,255,255,0.82)',
  boxShadow: '0 5px 0 rgba(76,29,149,0.30), 0 12px 22px rgba(124,58,237,0.20)',
  textTransform: 'none',
  fontWeight: 1000,
  fontSize: { xs: 18, sm: 20, md: 22 },
  lineHeight: 1.08,
  '& .MuiButton-startIcon': {
    mr: { xs: 0.8, md: 1 },
    '& svg': { fontSize: { xs: 25, md: 28 } },
  },
  '&:hover': {
    bgcolor: '#6d28d9',
    transform: 'translateY(-2px)',
    boxShadow: '0 7px 0 rgba(76,29,149,0.28), 0 16px 26px rgba(124,58,237,0.24)',
  },
  '&:disabled': {
    bgcolor: '#cbd5e1',
    color: '#64748b',
    borderColor: '#e2e8f0',
    boxShadow: 'none',
  },
  transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
};

export { buildWordBuildPreviewAttempt } from './word-build/wordBuildPreview';

export default function MiniTaskBoardPage() {
  const { token } = useParams();
  const loadBookEvents = useCallback((after) => fetchBookPointEvents(token, after), [token]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewId = Number(searchParams.get('preview_id'));
  const previewLevel = searchParams.has('preview_level') ? Number(searchParams.get('preview_level')) : null;
  const previewQuestionId = searchParams.get('preview_question_id')?.trim() || null;
  const previewEnabled = Number.isInteger(previewId) && previewId > 0
    && (previewLevel === null || (Number.isInteger(previewLevel) && previewLevel > 0));
  const [board, setBoard] = useState(null);
  const [activeRound, setActiveRound] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const activeQuestionKey = useRef(null);
  activeQuestionKey.current = `${activeRound?.round_id}:${currentQuestion?.id}`;
  const [feedback, setFeedback] = useState(null);
  const [roundSummary, setRoundSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyStudentId, setBusyStudentId] = useState(null);
  const [submittingChoice, setSubmittingChoice] = useState(null);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [roundAnswers, setRoundAnswers] = useState([]);
  const roundAnswersRef = useRef([]);
  const timeoutInFlightRef = useRef(false);
  const [timeoutState, setTimeoutState] = useState({ expired: false, saving: false, error: '' });
  const [roundCorrectCount, setRoundCorrectCount] = useState(0);
  const [roundReady, setRoundReady] = useState(false);
  const [roundPreparationError, setRoundPreparationError] = useState('');
  const [progressRecovering, setProgressRecovering] = useState(false);
  const activeRoundIdRef = useRef(null);
  activeRoundIdRef.current = activeRound?.round_id;
  const [preparationNonce, setPreparationNonce] = useState(0);
  const [assetUrls, setAssetUrls] = useState({});
  const assetUrlsRef = useRef({});
  const preparedRoundRef = useRef(null);
  const wordBuildAttemptsRef = useRef([]);
  const [soundEnabled, setSoundEnabled] = useState(() => readMiniTaskSoundEnabled());
  const [activeTournament, setActiveTournament] = useState(null);
  const [tournamentChoiceOpen, setTournamentChoiceOpen] = useState(false);
  const [tournamentBusy, setTournamentBusy] = useState(false);
  const [starGardenOpen, setStarGardenOpen] = useState(false);
  const [starGardenLoading, setStarGardenLoading] = useState(false);
  const [starGardenError, setStarGardenError] = useState('');
  const [starGarden, setStarGarden] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState('');
  const [library, setLibrary] = useState(null);
  const [timerOpen, setTimerOpen] = useState(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [balloonOpen, setBalloonOpen] = useState(false);
  const [trainOpen, setTrainOpen] = useState(false);
  const [teacherPinOpen, setTeacherPinOpen] = useState(false);
  const [teacherPin, setTeacherPin] = useState('');
  const [teacherPinError, setTeacherPinError] = useState('');
  const [teacherPinBusy, setTeacherPinBusy] = useState(false);
  const [breakNotice, setBreakNotice] = useState(null);
  const [boardReceivedAt, setBoardReceivedAt] = useState(Date.now);
  const [boardClock, setBoardClock] = useState(Date.now);
  const { beginRequest: beginBoardLoad, invalidateRequests: invalidateBoardLoads } = useLatestRequestGuard();

  const boardSessionId = board?.board_session?.id;
  const listeningAnswer = useListeningAnswer({ round: activeRound, question: currentQuestion, boardSessionId, boardToken: token });
  const students = useMemo(() => (board?.students || []).map((student) => ({
    ...student,
    cooldown_remaining_seconds: student.cooldown_until
      ? Math.max(1, Number(student.cooldown_remaining_seconds || 0) - (boardClock - boardReceivedAt) / 1000)
      : 0,
  })), [board?.students, boardClock, boardReceivedAt]);
  const randomStudentCandidates = useMemo(() => {
    const absentIds = new Set((board?.absent_student_ids || []).map(String));
    return students.filter((student) => !absentIds.has(String(student.id)));
  }, [board?.absent_student_ids, students]);
  const randomStudentSelector = useRandomStudentSelector(randomStudentCandidates, soundEnabled);

  const tournamentPath = `/mini-tasks/${token}/turnuva`;
  const closeTeacherPin = () => {
    setTeacherPinOpen(false);
    setTeacherPin('');
    setTeacherPinError('');
  };
  const loginTeacher = async (event) => {
    event.preventDefault();
    if (!/^[0-9]{4}$/.test(teacherPin)) return;
    setTeacherPinBusy(true);
    setTeacherPinError('');
    try {
      const result = await teacherRequest(token, 'login', null, { method: 'POST', body: JSON.stringify({ pin: teacherPin }) });
      saveTeacherSession(token, result.session_token);
      closeTeacherPin();
      navigate(`/mini-tasks/${token}/ogretmen`);
    } catch (err) {
      setTeacherPinError(err.message);
      setTeacherPin('');
    } finally {
      setTeacherPinBusy(false);
    }
  };
  const openStarGarden = useCallback(async () => {
    if (!token || !board?.class_story?.stars?.interaction?.enabled) return;
    setStarGardenOpen(true);
    setStarGardenLoading(true);
    setStarGardenError('');
    try {
      const payload = await fetchReadonlyStarGarden(token);
      setStarGarden(payload);
    } catch (err) {
      setStarGardenError(err?.message || 'Yıldız Bahçem yüklenemedi.');
    } finally {
      setStarGardenLoading(false);
    }
  }, [board?.class_story?.stars?.interaction?.enabled, token]);

  const closeStarGarden = () => setStarGardenOpen(false);

  const openLibrary = useCallback(async () => {
    if (!token || !board?.class_story?.library?.interaction?.enabled) return;
    setLibraryOpen(true);
    setLibraryLoading(true);
    setLibraryError('');
    try {
      const payload = await fetchReadonlyLibrary(token);
      setLibrary(payload);
    } catch (err) {
      setLibraryError(err?.message || 'Kütüphanemiz yüklenemedi.');
    } finally {
      setLibraryLoading(false);
    }
  }, [board?.class_story?.library?.interaction?.enabled, token]);

  const closeLibrary = () => setLibraryOpen(false);

  const openTournament = async () => {
    if (!boardSessionId) return;
    setTournamentBusy(true);
    try {
      const payload = await getCurrentTournament(token, boardSessionId);
      if (payload.tournament?.status === 'active') {
        setActiveTournament(payload.tournament);
        setTournamentChoiceOpen(true);
      } else {
        navigate(tournamentPath, {
          state: payload.tournament?.status === 'completed' ? { startNewTournament: true } : undefined,
        });
      }
    } catch (err) {
      setError(err?.message || 'Turnuva durumu kontrol edilemedi.');
    } finally {
      setTournamentBusy(false);
    }
  };

  const abandonAndStartNewTournament = async () => {
    if (!activeTournament || !boardSessionId) return;
    setTournamentBusy(true);
    try {
      await abandonTournament(token, boardSessionId, activeTournament.id);
      window.localStorage.removeItem(localTournamentStorageKey(token));
      setTournamentChoiceOpen(false);
      setActiveTournament(null);
      navigate(tournamentPath);
    } catch (err) {
      setError(err?.message || 'Turnuva sonlandırılamadı.');
    } finally {
      setTournamentBusy(false);
    }
  };

  const loadBoard = useCallback(async () => {
    if (!token) return;
    const request = beginBoardLoad();
    setLoading(true);
    setError('');
    try {
      const stored = await readStoredBoardSession(token);
      const payload = await fetchBoard(token, stored?.board_session_id);
      if (!request.isCurrent()) return;
      await writeStoredBoardSession(token, payload.board_session);
      if (!request.isCurrent()) return;
      setBoard(payload);
      setBoardReceivedAt(Date.now());
      setBoardClock(Date.now());
      setBreakNotice(null);
      setActiveRound(null);
      wordBuildAttemptsRef.current = [];
      setRoundReady(false);
      preparedRoundRef.current = null;
      releaseMiniTaskRoundAssets(assetUrlsRef.current);
      assetUrlsRef.current = {};
      setAssetUrls({});
      setCurrentQuestion(null);
      setFeedback(null);
      setRoundSummary(null);
      setSelectedChoice(null);
      setCorrectAnswer(null);
      if (previewEnabled) {
        const previewStudent = payload.students?.[0];
        if (!previewStudent?.id) {
          throw new Error('Preview için aktif öğrenci bulunamadı.');
        }
        const previewPayload = previewQuestionId
          ? await previewRound(
            token,
            previewStudent.id,
            payload.board_session.id,
            previewId,
            previewLevel,
            previewQuestionId,
          )
          : await previewRound(token, previewStudent.id, payload.board_session.id, previewId, previewLevel);
        if (!request.isCurrent()) return;
        setActiveRound(previewPayload);
        setCurrentQuestion(previewPayload.questions?.[0] || null);
        setRoundAnswers([]);
        roundAnswersRef.current = [];
        setTimeoutState({ expired: false, saving: false, error: '' });
        setRoundCorrectCount(0);
        setRoundSummary(previewPayload.summary || null);
        return;
      }
      const activeWordBuildRoundId = previewEnabled ? null : readActiveWordBuildRound(payload.board_session.id);
      if (activeWordBuildRoundId) {
        try {
          const recovered = await recoverWordBuildRound(activeWordBuildRoundId, payload.board_session.id);
          if (!request.isCurrent()) return;
          if (recovered.status === 'completed' && recovered.summary) {
            clearActiveWordBuildRound(payload.board_session.id);
            clearWordBuildLocalProgress(activeWordBuildRoundId);
            setActiveRound(recovered);
            setRoundSummary(recovered.summary);
          } else {
            const local = readWordBuildLocalProgress(activeWordBuildRoundId);
            if (recovered.mode === 'local_batch' && local?.round?.round_id === recovered.round_id
              && Array.isArray(local.attempts)) {
              wordBuildAttemptsRef.current = local.attempts;
              setActiveRound(local.round);
            } else {
              setActiveRound(recovered);
            }
          }
        } catch (_recoveryError) {
          if (!request.isCurrent()) return;
          clearActiveWordBuildRound(payload.board_session.id);
          clearWordBuildLocalProgress(activeWordBuildRoundId);
        }
      }
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(err?.message || 'Mini Görev ekranı açılamadı.');
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginBoardLoad, previewEnabled, previewId, previewLevel, previewQuestionId, token]);

  useEffect(() => {
    loadBoard();
    return invalidateBoardLoads;
  }, [invalidateBoardLoads, loadBoard]);

  useEffect(() => () => releaseMiniTaskRoundAssets(assetUrlsRef.current), []);

  const openRound = useCallback((payload) => {
    setRoundReady(false);
    preparedRoundRef.current = null;
    setActiveRound(payload);
    if (payload.task_type === 'word_build' && !payload.preview) {
      if (payload.mode === 'local_batch') writeWordBuildLocalProgress(payload, []);
      writeActiveWordBuildRound(boardSessionId, payload.round_id);
    }
    const restored = restoredListeningProgress(payload);
    setCurrentQuestion(restored?.question || payload.questions?.[0] || null);
    setFeedback(null);
    setSelectedChoice(null);
    setCorrectAnswer(null);
    setSubmittingChoice(null);
    setRoundAnswers(restored?.answers || []);
    roundAnswersRef.current = restored?.answers || [];
    setTimeoutState({ expired: false, saving: false, error: '' });
    setRoundCorrectCount(restored?.correctCount || 0);
    wordBuildAttemptsRef.current = [];
    setRoundSummary(payload.summary || null);
  }, [boardSessionId]);

  const showStudentBreak = useCallback((studentId, detail) => {
    setBreakNotice({ ...detail, student_id: studentId });
    setBoard((current) => ({
      ...current,
      students: current.students.map((item) => String(item.id) === String(studentId)
        ? { ...item, ...detail } : item),
    }));
    setBoardReceivedAt(Date.now());
    setBoardClock(Date.now());
  }, []);

  const recoverCurrentActivity = useCallback(async (outdatedRound) => {
    clearMiniTaskCountdown(outdatedRound.round_id);
    preparedRoundRef.current = null;
    setRoundReady(false);
    setProgressRecovering(true);
    setRoundPreparationError('');
    try {
      const payload = await startRound(token, outdatedRound.student.id, boardSessionId);
      if (activeRoundIdRef.current !== outdatedRound.round_id) return;
      if (String(payload.round_id) === String(outdatedRound.round_id)) {
        throw new Error('Güncel etkinliğin alınamadı. Yeniden dene.');
      }
      openRound(payload);
    } catch (err) {
      if (activeRoundIdRef.current === outdatedRound.round_id) {
        if (err?.detail?.code === 'smartboard_break') {
          setActiveRound(null);
          setCurrentQuestion(null);
          setFeedback(null);
          setSubmittingChoice(null);
          showStudentBreak(outdatedRound.student.id, err.detail);
          return;
        }
        setRoundPreparationError(err.message || 'Güncel etkinliğin alınamadı. Yeniden dene.');
      }
    } finally {
      setProgressRecovering(false);
    }
  }, [token, boardSessionId, openRound, showStudentBreak]);

  useEffect(() => {
    if (!activeRound || roundSummary || activeRound.task_type === 'weekly_reading') return undefined;
    const roundId = String(activeRound.round_id);
    if (preparedRoundRef.current === roundId) return undefined;
    let active = true;
    setRoundReady(false);
    setRoundPreparationError('');
    (async () => {
      let urls = null;
      try {
        urls = await prepareMiniTaskRoundAssets(activeRound);
        if (!active) {
          releaseMiniTaskRoundAssets(urls);
          return;
        }
        if (!activeRound.preview && !activeRound.listening_contract && activeRound.status !== 'completed') {
          await beginPreparedRound(activeRound.round_id, boardSessionId);
        }
        if (!active) {
          releaseMiniTaskRoundAssets(urls);
          return;
        }
        releaseMiniTaskRoundAssets(assetUrlsRef.current);
        assetUrlsRef.current = urls;
        setAssetUrls(urls);
        preparedRoundRef.current = roundId;
        setRoundReady(true);
      } catch (err) {
        releaseMiniTaskRoundAssets(urls);
        if (active && isProgressChangedError(err)) {
          await recoverCurrentActivity(activeRound);
          return;
        }
        if (active) setRoundPreparationError(err.message || 'Görev hazırlanamadı.');
      }
    })();
    return () => { active = false; };
  }, [activeRound, boardSessionId, preparationNonce, roundSummary, recoverCurrentActivity]);

  const classTitle = useMemo(() => {
    const code = board?.class?.code || '';
    return code ? `${code} Sınıfı` : 'Mini Görevler';
  }, [board?.class?.code]);

  // Pause deadlines are deterministic. Confirm with the server only when the
  // earliest break expires; do not GET /board every 30 seconds on idle boards.
  // Cards stay disabled until that server confirmation succeeds.
  useEffect(() => {
    if (!token || !boardSessionId || activeRound || roundSummary || busyStudentId || previewEnabled) return undefined;
    let cancelled = false;
    let inFlight = false;
    let deadlineTimer = null;
    const hasPausedCards = (board?.students || []).some((student) => Boolean(student.cooldown_until));
    const refresh = async () => {
      if (cancelled || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const payload = await fetchBoard(token, boardSessionId);
        if (!cancelled) {
          const receivedAt = Date.now();
          setBoard(payload);
          setBoardReceivedAt(receivedAt);
          setBoardClock(receivedAt);
          setBreakNotice((notice) => payload.students?.some(
            (student) => String(student.id) === String(notice?.student_id) && student.cooldown_until,
          ) ? notice : null);
        }
      } catch (_) {
        // Keep the last known card state, but retry a failed expiry check.
        // Idle boards with no breaks have nothing to retry.
        if (!cancelled && hasPausedCards) {
          deadlineTimer = window.setTimeout(refresh, BOARD_COOLDOWN_RETRY_MS);
        }
      } finally {
        inFlight = false;
      }
    };
    if (hasPausedCards) {
      const delay = nextBoardCooldownRefreshDelay(board.students, boardReceivedAt);
      deadlineTimer = window.setTimeout(refresh, delay);
    }
    // This is strictly local UI countdown work; it does not call the API.
    const clockTimer = hasPausedCards
      ? window.setInterval(() => setBoardClock(Date.now()), 30000) : null;
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      if (deadlineTimer !== null) window.clearTimeout(deadlineTimer);
      if (clockTimer !== null) window.clearInterval(clockTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', refresh);
    };
  }, [token, boardSessionId, activeRound, roundSummary, busyStudentId, previewEnabled,
    board?.students, boardReceivedAt]);

  const handleToggleSound = () => {
    setSoundEnabled((current) => {
      const next = !current;
      writeMiniTaskSoundEnabled(next);
      if (next) playMiniTaskSound('correct', true);
      return next;
    });
  };

  const handleStudentClick = async (student) => {
    if (!token || !boardSessionId || !student?.id) return;
    if (!previewEnabled && student.cooldown_until) return false;
    prepareMiniTaskSound(soundEnabled);
    setBusyStudentId(student.id);
    setRoundReady(false);
    preparedRoundRef.current = null;
    setError('');
    try {
      const payload = previewEnabled
        ? (previewQuestionId
          ? await previewRound(token, student.id, boardSessionId, previewId, previewLevel, previewQuestionId)
          : await previewRound(token, student.id, boardSessionId, previewId, previewLevel))
        : await startRound(token, student.id, boardSessionId);
      openRound(payload);
      return true;
    } catch (err) {
      if (err?.detail?.code === 'smartboard_break') {
        showStudentBreak(student.id, err.detail);
        return false;
      }
      setError(err?.message || 'Mini görev başlatılamadı.');
      return false;
    } finally {
      setBusyStudentId(null);
    }
  };

  const handleAnswer = async (choice) => {
    if (!activeRound?.round_id || !currentQuestion?.id || !boardSessionId) return;
    const listeningResult = activeRound.listening_contract ? await listeningAnswer.grade(choice) : null;
    if (activeRound.listening_contract && !listeningResult) return;
    const isSequence = currentQuestion.answer_mode === 'sequence_builder';
    let sequenceResult = null;
    if (isSequence) {
      if (!activeRound.preview || !activeRound.presentation_snapshot_id || !Array.isArray(choice?.selectedTokenIds)) {
        throw new Error('Sıralama için sunucu değerlendirmesi hazır değil.');
      }
      const submittedQuestionKey = activeQuestionKey.current;
      sequenceResult = await recordMathPresentationAnswer(token, activeRound.presentation_snapshot_id, boardSessionId, currentQuestion.id, choice);
      if (activeQuestionKey.current !== submittedQuestionKey) return;
      if (typeof sequenceResult?.is_correct !== 'boolean' || !Array.isArray(sequenceResult.correct_display_order) || !Array.isArray(sequenceResult.correct_token_order)) {
        throw new Error('Sıralama değerlendirmesi alınamadı.');
      }
    } else if (activeRound.preview && activeRound.presentation_snapshot_id && currentQuestion.presentation_version) {
      recordMathPresentationAnswer(token, activeRound.presentation_snapshot_id, boardSessionId, currentQuestion.id, choice)
        .catch((error) => console.warn('Önizleme sunum telemetrisi kaydedilemedi.', error));
    }
    setSubmittingChoice(choice);
    setSelectedChoice(choice);
    setCorrectAnswer(null);
    setFeedback(null);
    const isCorrect = listeningResult ? listeningResult.is_correct : isSequence ? sequenceResult.is_correct : String(choice) === String(currentQuestion.correct_answer);
    const answerText = listeningResult ? listeningResult.correct_answer : isSequence ? sequenceResult.correct_display_order.join(', ') : currentQuestion.correct_answer;
    const nextCorrectCount = roundCorrectCount + (isCorrect ? 1 : 0);
    const questions = activeRound.questions || [];
    const currentIndex = questions.findIndex((question) => question.id === currentQuestion.id);
    const nextQuestion = questions[currentIndex + 1] || null;
    const nextAnswers = [
      ...roundAnswers,
      {
        question_id: currentQuestion.id,
        selected_answer: choice,
        ...(isSequence ? { is_correct: isCorrect } : {}),
      },
    ];
    setCorrectAnswer(answerText);
    const isEnglishRound = activeRound?.lesson?.code === 'ENGLISH';
    setFeedback({
      ...(isSequence ? { correct_token_order: sequenceResult.correct_token_order } : {}),
      type: isCorrect ? 'correct' : 'near',
      message: isEnglishRound
        ? (isCorrect ? 'Great job!' : `Almost! The correct answer is ${answerText}.`)
        : (isCorrect ? 'Harika, doğru!' : `Neredeyse! Doğru cevap ${answerText}.`),
    });
    setRoundAnswers(nextAnswers);
    roundAnswersRef.current = nextAnswers;
    setRoundCorrectCount(nextCorrectCount);
    playMiniTaskSound(isCorrect ? 'correct' : 'wrong', soundEnabled);

    if (nextQuestion) {
      window.setTimeout(() => {
        setCurrentQuestion(nextQuestion);
        setFeedback(null);
        setSelectedChoice(null);
        setCorrectAnswer(null);
        setSubmittingChoice(null);
      }, ANSWER_FEEDBACK_DELAY_MS);
      return;
    }

    if (activeRound.listening_contract) return; // Server-scored QA answers never trigger activation/rewards.
    try {
      const summary = activeRound.preview
        ? {
          preview: true,
          task_type: 'multiple_choice',
          correct_count: nextCorrectCount,
          question_count: questions.length,
          rounds_remaining: 1,
          next_prompt: isEnglishRound ? 'Preview complete!' : 'Test turu tamamlandı!',
          locale: isEnglishRound ? 'en' : 'tr',
        }
        : (await completeRound(activeRound.round_id, boardSessionId, nextAnswers)).summary;
      clearMiniTaskCountdown(activeRound.round_id);
      window.setTimeout(() => {
        playMiniTaskSound('complete', soundEnabled);
        setCurrentQuestion(null);
        setRoundSummary(summary);
        setSubmittingChoice(null);
      }, ANSWER_FEEDBACK_DELAY_MS);
    } catch (err) {
      if (isProgressChangedError(err)) {
        await recoverCurrentActivity(activeRound);
        return;
      }
      setFeedback({ type: 'near', message: err?.message || (isEnglishRound ? 'The result could not be saved.' : 'Tur sonucu kaydedilemedi.') });
      setRoundAnswers(roundAnswers);
      roundAnswersRef.current = roundAnswers;
      setRoundCorrectCount(roundCorrectCount);
      setSubmittingChoice(null);
      window.setTimeout(() => {
        setFeedback(null);
        setSelectedChoice(null);
        setCorrectAnswer(null);
      }, 1800);
    }
  };

  const handleRoundTimeout = async () => {
    if (activeRound?.listening_contract || currentQuestion?.listening) return;
    if (!activeRound?.round_id || !boardSessionId || timeoutInFlightRef.current) return;
    timeoutInFlightRef.current = true;
    setTimeoutState({ expired: true, saving: true, error: '' });
    try {
      const summary = activeRound.preview
        ? {
          preview: true,
          task_type: 'multiple_choice',
          finish_reason: 'timer',
          correct_count: roundAnswersRef.current.filter((answer) => {
            if (typeof answer.is_correct === 'boolean') return answer.is_correct;
            const question = activeRound.questions?.find((item) => item.id === answer.question_id);
            return String(answer.selected_answer) === String(question?.correct_answer);
          }).length,
          question_count: activeRound.questions?.length || 5,
          rounds_remaining: 1,
          next_prompt: 'Süre doldu. Test turu tamamlandı.',
        }
        : (await completeRound(activeRound.round_id, boardSessionId, roundAnswersRef.current, 'timer')).summary;
      clearMiniTaskCountdown(activeRound.round_id);
      setCurrentQuestion(null);
      setRoundSummary(summary);
    } catch (err) {
      if (isProgressChangedError(err)) {
        await recoverCurrentActivity(activeRound);
        return;
      }
      setTimeoutState({ expired: true, saving: false, error: err?.message || 'Sonuç kaydedilemedi.' });
    } finally {
      timeoutInFlightRef.current = false;
    }
  };

  const handleBackToBoard = async () => {
    await loadBoard();
  };

  const handleWordBuildExit = async () => {
    if (boardSessionId) clearActiveWordBuildRound(boardSessionId);
    if (activeRound?.round_id) clearWordBuildLocalProgress(activeRound.round_id);
    await loadBoard();
  };

  const handleWordBuildAttempt = ({ questionId, answer, idempotencyKey }) => {
    if (!activeRound?.round_id || !boardSessionId) {
      throw new Error('Tur bilgisi bulunamadı.');
    }
    if (activeRound.preview) {
      return buildWordBuildPreviewAttempt(activeRound, questionId, answer);
    }
    const { attempts, result } = playLocalWordBuildAttempt(
      activeRound, boardSessionId, wordBuildAttemptsRef.current,
      { questionId, answer, idempotencyKey },
    );
    wordBuildAttemptsRef.current = attempts;
    return result;
  };

  const handleWordBuildComplete = (summary) => {
    if (boardSessionId) clearActiveWordBuildRound(boardSessionId);
    if (activeRound?.round_id) clearWordBuildLocalProgress(activeRound.round_id);
    setRoundSummary(summary);
  };

  const handleWordBuildTimeout = async () => {
    if (!activeRound?.round_id || !boardSessionId) throw new Error('Tur bilgisi bulunamadı.');
    if (activeRound.preview) {
      return {
        preview: true,
        task_type: 'word_build',
        finish_reason: 'timer',
        completed_word_count: Number(activeRound.completed_word_count || 0),
        rounds_remaining: 1,
        next_prompt: 'Test turu tamamlandı!',
      };
    }
    const attempts = wordBuildAttemptsRef.current.map(({ question_id, answer }) => ({ question_id, answer }));
    const payload = await timeoutWordBuildRound(activeRound.round_id, boardSessionId, attempts);
    return payload.summary;
  };

  const handleMemoryExit = async () => {
    await loadBoard();
  };

  const handleMemoryComplete = async (attemptCounts, finishReason = 'completed', matchedCounts = null) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      setRoundSummary({
        preview: true,
        task_type: 'memory_matching',
        finish_reason: finishReason,
        rounds_remaining: 1,
        next_prompt: 'Test turu tamamlandı!',
      });
      return;
    }
    const payload = await completeMemoryRound(activeRound.round_id, boardSessionId, attemptCounts, finishReason, matchedCounts);
    setRoundSummary(payload.summary);
  };

  const handleDigitRecallExit = async () => {
    await loadBoard();
  };

  const handleDigitRecallComplete = async (responses, finishReason = 'completed') => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      setRoundSummary({
        preview: true,
        task_type: 'digit_recall',
        finish_reason: finishReason,
        exact_sequence_count: responses.filter((response, index) => String(response) === String(activeRound.sequences?.[index])).length,
        sequence_count: Number(activeRound.sequence_count || responses.length),
        rounds_remaining: 1,
        next_prompt: 'Test turu tamamlandı!',
      });
      return;
    }
    const payload = await completeDigitRecallRound(activeRound.round_id, boardSessionId, responses, finishReason);
    setRoundSummary(payload.summary);
  };

  const handleReflexExit = async () => {
    await loadBoard();
  };

  const handleReflexComplete = async (score, interactionCount) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      setRoundSummary({
        preview: true,
        task_type: 'reflex_catch',
        score: Number(score),
        rounds_remaining: 0,
        next_prompt: 'Test turu tamamlandı!',
      });
      return;
    }
    const payload = await completeReflexRound(activeRound.round_id, boardSessionId, score, interactionCount);
    setRoundSummary(payload.summary);
  };

  const handleOddOneOutExit = () => {
    setActiveRound(null);
    setCurrentQuestion(null);
    setRoundSummary(null);
    setFeedback(null);
  };

  const handleOddOneOutComplete = async (events, finishReason) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      const score = countCorrectEvents(activeRound.boards, events);
      setRoundSummary({
        preview: true,
        task_type: 'odd_one_out',
        score,
        accuracy_pct: events.length ? Math.round((score / events.length) * 100) : 0,
        rounds_remaining: 1,
        next_prompt: 'Test turu tamamlandı!',
      });
      return;
    }
    const payload = await completeOddOneOutRound(activeRound.round_id, boardSessionId, events, finishReason);
    setRoundSummary(payload.summary);
  };

  const handleCargoSortingComplete = async (counts) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      const reachedTarget = Number(counts.correct_count) >= Number(activeRound.target_correct || 0);
      setRoundSummary({
        preview: true,
        task_type: 'cargo_sorting',
        ...counts,
        target_correct: Number(activeRound.target_correct || 0),
        reached_target: reachedTarget,
        rounds_remaining: 1,
        next_prompt: reachedTarget ? 'Harika! Hedefe ulaştın.' : 'Güzel deneme! Görev tamamlandı.',
      });
      return;
    }
    const payload = await completeCargoSortingRound(activeRound.round_id, boardSessionId, counts);
    setRoundSummary(payload.summary);
  };

  const handlePatternRecognitionComplete = async (rounds) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    if (activeRound.preview) {
      setRoundSummary(patternRecognitionPreviewSummary(
        [activeRound.questions || []],
        rounds,
      ));
      return;
    }
    const payload = await completePatternRecognitionRound(activeRound.round_id, boardSessionId, rounds);
    setRoundSummary(payload.summary);
  };

  useEffect(() => {
    if (!roundSummary || !previewEnabled || previewLevel !== null
      || !activeRound?.preview_level || activeRound.next_round
      || activeRound.preview_level >= activeRound.preview_max_level) return undefined;
    let cancelled = false;
    previewRound(token, activeRound.student.id, boardSessionId, previewId, activeRound.preview_level + 1)
      .then((payload) => {
        if (cancelled) return;
        setActiveRound(payload);
        setCurrentQuestion(payload.questions?.[0] || null);
        setRoundSummary(null);
        setFeedback(null);
        setSelectedChoice(null);
        setCorrectAnswer(null);
        setRoundAnswers([]);
        roundAnswersRef.current = [];
        setRoundCorrectCount(0);
        setSubmittingChoice(null);
      }).catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [roundSummary, previewEnabled, previewLevel, activeRound, token, boardSessionId, previewId]);

  const handleContinue = async () => {
    const studentId = activeRound?.student?.id;
    const student = (board?.students || []).find((item) => String(item.id) === String(studentId));
    if (student) {
      return handleStudentClick(student);
    } else {
      await loadBoard();
      return true;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fffaf0' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !board) {
    return <BoardErrorState message={error} onRetry={loadBoard} />;
  }

  if (roundSummary) {
    return <RoundSummary summary={roundSummary} onBackToBoard={handleBackToBoard} onContinue={handleContinue}
      backToBoardLabel={activeRound?.preview_id === 61 ? 'Önizlemeyi yeniden başlat' : null}
      breakMinutes={roundSummary.cooldown_until ? Math.ceil(Number(roundSummary.cooldown_remaining_seconds || 1800) / 60) : null} />;
  }

  if (activeRound && activeRound.task_type !== 'weekly_reading' && !roundReady) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#fffaf0', px: 2 }}>
        <Box sx={{ textAlign: 'center', color: '#5b21b6' }}>
          {!roundPreparationError && <CircularProgress sx={{ mb: 2 }} />}
          <Typography role={roundPreparationError ? 'alert' : 'status'} sx={{ fontSize: 26, fontWeight: 900 }}>
            {roundPreparationError || (progressRecovering ? 'Güncel etkinliğin hazırlanıyor...' : 'Sorular ve görseller hazırlanıyor...')}
          </Typography>
          {roundPreparationError && <Button disableRipple onClick={() => setPreparationNonce((value) => value + 1)}
            sx={{ mt: 2, minHeight: 44, fontSize: 20, fontWeight: 900, color: '#5b21b6',
              '&:hover': { bgcolor: '#ede9fe' },
              '&.Mui-focusVisible': { bgcolor: '#ede9fe', outline: '3px solid #5b21b6', outlineOffset: 3 },
            }}>Yeniden Dene</Button>}
        </Box>
      </Box>
    );
  }

  if (activeRound?.task_type === 'word_build') {
    return (
      <WordBuildRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        assetUrls={assetUrls}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleWordBuildExit}
        onAttempt={handleWordBuildAttempt}
        onRoundChange={setActiveRound}
        onComplete={handleWordBuildComplete}
        onTimeout={handleWordBuildTimeout}
      />
    );
  }

  if (activeRound?.task_type === 'weekly_reading') {
    return (
      <WeeklyReadingRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        previewToken={token}
        classTitle={classTitle}
        onExit={handleBackToBoard}
      />
    );
  }

  if (activeRound?.task_type === 'memory_matching') {
    return (
      <MemoryRoundView
        round={activeRound}
        assetUrls={assetUrls}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleMemoryExit}
        onComplete={handleMemoryComplete}
      />
    );
  }

  if (activeRound?.task_type === 'reflex_catch') {
    return (
      <ReflexRoundView
        round={activeRound}
        assetUrls={assetUrls}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleReflexExit}
        onComplete={handleReflexComplete}
      />
    );
  }

  if (activeRound?.task_type === 'digit_recall') {
    return (
      <DigitRecallRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleDigitRecallExit}
        onComplete={handleDigitRecallComplete}
      />
    );
  }

  if (activeRound?.task_type === 'odd_one_out') {
    return (
      <OddOneOutRoundView
        round={activeRound}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleOddOneOutExit}
        onComplete={handleOddOneOutComplete}
      />
    );
  }

  if (activeRound?.task_type === 'turkish_l1_tour') {
    return <TurkishL1TourView key={activeRound.round_id} round={activeRound} boardSessionId={boardSessionId}
      assetUrls={assetUrls} classTitle={classTitle} onComplete={setRoundSummary} onExit={handleBackToBoard} />;
  }
  if (['sound_hunt', 'letter_hunt', 'sound_discovery', 'target_sound_hunt'].includes(activeRound?.task_type)) {
    return <TurkishL1RoundView key={activeRound.round_id} round={activeRound} boardSessionId={boardSessionId}
      assetUrls={assetUrls} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={handleToggleSound}
      onComplete={setRoundSummary} onExit={handleBackToBoard} />;
  }

  if (activeRound?.task_type === 'cargo_sorting') {
    return (
      <CargoSortingRoundView
        round={activeRound}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleBackToBoard}
        onComplete={handleCargoSortingComplete}
      />
    );
  }

  if (activeRound?.task_type === 'pattern_recognition') {
    return (
      <PatternRecognitionRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleBackToBoard}
        onComplete={handlePatternRecognitionComplete}
      />
    );
  }

  if (activeRound && currentQuestion) {
    return (
      <RoundView
        round={activeRound}
        boardToken={token}
        answerPending={listeningAnswer.grading}
        answerError={listeningAnswer.error}
        boardSessionId={boardSessionId}
        onTimeout={handleRoundTimeout}
        timedOut={timeoutState.expired}
        timeoutSaving={timeoutState.saving}
        timeoutError={timeoutState.error}
        timerActive={!Boolean(submittingChoice)}
        assetUrls={assetUrls}
        question={currentQuestion}
        feedback={feedback}
        submittingChoice={submittingChoice}
        selectedChoice={selectedChoice}
        correctAnswer={correctAnswer}
        classTitle={activeRound.preview && /^curriculum_(?:math|turkish)_[2-8]$/.test(activeRound.skill_key || '')
          ? `${activeRound.skill_key.split('_').at(-1)}. Sınıf Önizleme`
          : classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onBackToBoard={handleBackToBoard}
        onAnswer={handleAnswer}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fffaf0',
        color: '#172554',
        overflow: 'hidden',
        backgroundImage:
          'radial-gradient(circle at 3% 96%, rgba(34,197,94,0.18) 0 60px, transparent 61px), radial-gradient(circle at 98% 96%, rgba(132,204,22,0.20) 0 74px, transparent 75px)',
      }}
    >
      <LibraryPointAnimations loadEvents={loadBookEvents} boardLive />
      <Box sx={{ mx: 'auto', maxWidth: 1720, px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 2, md: 3 } }}>
        <BoardHeader
          classTitle={classTitle}
          showTeacherButton={!previewEnabled}
          onTeacherClick={() => { setTeacherPin(''); setTeacherPinError(''); setTeacherPinOpen(true); }}
          onRefresh={loadBoard}
          soundEnabled={soundEnabled}
          onToggleSound={handleToggleSound}
        />

        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, flexWrap: 'wrap', gap: 1.5, mb: { xs: 1.6, md: 1.8 } }}>
          <Button
            variant="contained"
            onClick={randomStudentSelector.run}
            disabled={!randomStudentCandidates.length}
            startIcon={<PersonSearchRoundedIcon />}
            sx={boardActionButtonSx}
          >
            Rastgele Öğrenci Seç
          </Button>
          {!previewEnabled && String(board?.class?.grade_level) === '1' && board?.activities?.word_wheel?.enabled && <Button
            variant="contained" onClick={() => setWheelOpen(true)} startIcon={<CasinoRoundedIcon />} sx={boardActionButtonSx}
          >Hece & Kelime Çarkı</Button>}
          {!previewEnabled && String(board?.class?.grade_level) === '1' && board?.activities?.classroom_balloon?.enabled && <Button
            variant="contained" onClick={() => setBalloonOpen(true)} sx={boardActionButtonSx}
          >Balon Patlat</Button>}
          {!previewEnabled && String(board?.class?.grade_level) === '1' && board?.activities?.classroom_balloon?.enabled && <Button
            variant="contained" onClick={() => setTrainOpen(true)} startIcon={<TrainRoundedIcon />} sx={boardActionButtonSx}
          >Hece Treni</Button>}
          <Button
            variant="contained"
            onClick={() => setTimerOpen(true)}
            startIcon={<TimerRoundedIcon />}
            sx={boardActionButtonSx}
          >
            Zamanlayıcı
          </Button>
          {!previewEnabled && (
            <Button
              onClick={openTournament}
              disabled={tournamentBusy}
              variant="contained"
              startIcon={<EmojiEventsRoundedIcon />}
              sx={boardActionButtonSx}
            >
              Turnuva
            </Button>
          )}
        </Box>

        <Dialog open={tournamentChoiceOpen} onClose={() => !tournamentBusy && setTournamentChoiceOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 1000, color: '#242061' }}>Devam eden turnuva var</DialogTitle>
          <DialogContent>
            <Typography>
              {activeTournament?.participant_count || 'Bu'} öğrencilik turnuva henüz tamamlanmadı. Devam edebilir veya sonlandırıp yeni bir turnuva hazırlayabilirsiniz.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
            <Button disabled={tournamentBusy} onClick={() => setTournamentChoiceOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>Vazgeç</Button>
            <Button variant="outlined" color="error" disabled={tournamentBusy} onClick={abandonAndStartNewTournament}
              sx={{ textTransform: 'none', fontWeight: 900 }}>Yeni Turnuva Hazırla</Button>
            <Button variant="contained" disabled={tournamentBusy} onClick={() => navigate(tournamentPath)}
              sx={{ textTransform: 'none', fontWeight: 900 }}>Devam Et</Button>
          </DialogActions>
        </Dialog>

        <TeacherPinDialog
          open={teacherPinOpen}
          pin={teacherPin}
          onPinChange={setTeacherPin}
          onClose={closeTeacherPin}
          onSubmit={loginTeacher}
          busy={teacherPinBusy}
          error={teacherPinError}
        />

        <ClassStoryPanel story={board?.class_story} onLibraryOpen={openLibrary} onStarGardenOpen={openStarGarden} />
        <LibraryReadOnlyDialog
          open={libraryOpen}
          onClose={closeLibrary}
          loading={libraryLoading}
          error={libraryError}
          library={library}
        />
        <StarGardenReadOnlyDialog
          open={starGardenOpen}
          onClose={closeStarGarden}
          loading={starGardenLoading}
          error={starGardenError}
          garden={starGarden}
        />

        <Typography
          sx={{
            textAlign: 'center',
            color: '#5b21b6',
            fontWeight: 1000,
            fontSize: { xs: 30, sm: 38, md: 44 },
            lineHeight: 1,
            mt: { xs: 2.1, md: 2.7 },
            mb: { xs: 1.5, md: 1.9 },
            textShadow: '0 3px 0 rgba(255,255,255,0.92)',
          }}
        >
          Kendi kartına dokun ve mini görevine başla
        </Typography>

        {error && (
          <Box sx={{ bgcolor: '#fffbeb', color: '#92400e', border: '2px solid #fde68a', borderRadius: 2, p: 1.2, mb: 1.5, fontWeight: 900 }}>
            {error}
          </Box>
        )}

        {students.some((student) => student.cooldown_until) && (
          <Box role="status" sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: '#eef2ff', border: '2px solid #c7d2fe', color: '#3730a3', textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, md: 22 } }}>
              🌙 {breakNotice ? 'Üç etkinliği tamamladın! Şimdi mola zamanı.' : 'Üç etkinlik, ardından küçük bir mola'}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: { xs: 16, md: 19 } }}>
              Mola veren kartlar yaklaşık 30 dakika sonra yeniden açılır. Şimdi sıra arkadaşlarında!
            </Typography>
          </Box>
        )}
        <StudentGrid students={students} onStudentClick={handleStudentClick} busyStudentId={busyStudentId} />

        <Box
          sx={{
            mt: { xs: 2, md: 2.7 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: '#242061',
            fontWeight: 900,
            fontSize: { xs: 16, md: 21 },
            textAlign: 'center',
          }}
        >
          <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#a78bfa', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.12)' }}>
            ♥
          </Box>
          Kartını bulamıyorsan öğretmeninden yardım iste.
        </Box>
      </Box>
      <RandomStudentSelectorModal
        open={randomStudentSelector.open}
        previousStudent={randomStudentSelector.previousStudent}
        displayStudent={randomStudentSelector.displayStudent}
        nextStudent={randomStudentSelector.nextStudent}
        selectedStudent={randomStudentSelector.selectedStudent}
        running={randomStudentSelector.running}
        statusText={randomStudentSelector.statusText}
        animationStep={randomStudentSelector.animationStep}
        onSelectAgain={randomStudentSelector.run}
        onClose={randomStudentSelector.close}
      />
      {timerOpen && <ClassroomTimerModal soundEnabled={soundEnabled} onClose={() => setTimerOpen(false)} />}
      {wheelOpen && <ClassroomActivity key={`${token}:wheel`} token={token} game="wheel" onClose={() => setWheelOpen(false)} soundEnabled={soundEnabled} />}
      {balloonOpen && <ClassroomActivity key={`${token}:balloon`} token={token} game="balloon" onClose={() => setBalloonOpen(false)} />}
      {trainOpen && <ClassroomTrainActivity key={`${token}:train`} token={token} onClose={() => setTrainOpen(false)} />}
    </Box>
  );
}
